import {
	Button,
	ButtonUtility,
	Modal,
	ModalOverlay,
	Table,
	TableCard,
	type TableColumn,
	TableRowActions,
	ToastCustom,
} from "@components";
import { Button as PageButton } from "@components/button/button-untitledui";
import { AlertCircle, Link03, LinkBroken02, XClose } from "@untitledui/icons";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { TRAILER_BODY_TYPE_IDS } from "../constants";
import {
	useDeleteVehicleAssignment,
	useInfiniteVehicleAssignments,
} from "../query";
import type { VehicleAssignment } from "../types";
import { AssignTrailerModal } from "./assign-trailer-modal";

const PAGE_SIZE = 5;

type AssignmentsCardProps = {
	vehicleId: number;
	vehiclePlate: string;
	bodyType: number;
};

function formatAssignmentDate(value: string): string {
	return dayjs(value).format("DD/MM/YYYY - HH:mm");
}

function getAssignedPlate(
	assignment: VehicleAssignment,
	currentVehicleId: number
): string {
	if (assignment.vehicle.id === currentVehicleId) {
		return assignment.trailerVehicle.plate;
	}
	return assignment.vehicle.plate;
}

function buildColumns(
	currentVehicleId: number,
	onRemoveClick: (assignment: VehicleAssignment) => void
): TableColumn<VehicleAssignment>[] {
	return [
		{
			id: "assignmentDate",
			label: "Data da Atribuição",
			render: (item) => formatAssignmentDate(item.assignmentDate),
		},
		{
			id: "removalDate",
			label: "Data da Retirada",
			render: (item) =>
				item.removalDate
					? formatAssignmentDate(item.removalDate)
					: "Atualmente",
		},
		{
			id: "assignedPlate",
			label: "Placa Atribuída",
			render: (item) => getAssignedPlate(item, currentVehicleId),
		},
		{
			id: "actions",
			label: "",
			render: (item) =>
				item.removalDate ? null : (
					<TableRowActions
						actions={[
							{
								icon: LinkBroken02,
								label: "Remover atribuição",
								onClick: () => onRemoveClick(item),
							},
						]}
					/>
				),
		},
	];
}

export const AssignmentsCard = ({
	vehicleId,
	vehiclePlate,
	bodyType,
}: AssignmentsCardProps) => {
	const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
	const [assignmentToRemove, setAssignmentToRemove] =
		useState<VehicleAssignment | null>(null);
	const sentinelRef = useRef<HTMLDivElement | null>(null);
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);

	const isTrailer = TRAILER_BODY_TYPE_IDS.has(bodyType);
	const canAssign = !isTrailer;

	const {
		data,
		isLoading,
		refetch,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteVehicleAssignments(String(vehicleId), PAGE_SIZE);
	const deleteAssignment = useDeleteVehicleAssignment();

	const items = useMemo(
		() => data?.pages.flatMap((assignmentsPage) => assignmentsPage.items) ?? [],
		[data]
	);
	const hasItems = items.length > 0;
	const columns = buildColumns(vehicleId, setAssignmentToRemove);
	const assignedTrailerIds = items
		.filter((item) => !item.removalDate)
		.map((item) => item.trailerVehicle.id);

	const description = isTrailer
		? "Vá até o veículo para fazer a atribuição de Reboque ou Semi-reboque"
		: "Clique em Atribuir a veículo";

	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!(sentinel && hasNextPage) || isFetchingNextPage) {
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					fetchNextPage();
				}
			},
			{ root: scrollContainerRef.current, threshold: 0.1 }
		);

		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	const handleConfirmRemove = async () => {
		if (!assignmentToRemove) {
			return;
		}

		try {
			await deleteAssignment.mutateAsync({
				vehicleAssignmentId: String(assignmentToRemove.id),
				vehicleId: String(vehicleId),
			});
			toast(
				ToastCustom({
					title: "Atribuição removida com sucesso",
					variant: "success",
				})
			);
			setAssignmentToRemove(null);
			refetch();
		} catch {
			toast(
				ToastCustom({
					title: "Não foi possível remover a atribuição",
					variant: "error",
				})
			);
		}
	};

	function renderContent() {
		if (isLoading) {
			return (
				<div className="flex min-h-40 items-center justify-center px-6 pb-6 text-gray-500 text-sm">
					Carregando...
				</div>
			);
		}

		if (hasItems) {
			return (
				<TableCard.Root className="mx-6 mb-6" variant="listing">
					<div
						className="max-h-[320px] overflow-y-auto"
						ref={scrollContainerRef}
					>
						<Table aria-label="Atribuições de veículo" selectionMode="none">
							<Table.Header columns={columns}>
								{(column) => (
									<Table.Head
										id={column.id}
										isRowHeader={columns.indexOf(column) === 0}
										key={column.id}
										label={column.label}
									/>
								)}
							</Table.Header>
							<Table.Body>
								{items.map((item) => (
									<Table.Row columns={columns} id={item.id} key={item.id}>
										{(column) => (
											<Table.Cell key={column.id}>
												{column.render?.(item)}
											</Table.Cell>
										)}
									</Table.Row>
								))}
							</Table.Body>
						</Table>

						{hasNextPage ? (
							<div aria-hidden="true" className="h-1" ref={sentinelRef} />
						) : null}

						{isFetchingNextPage ? (
							<div className="flex items-center justify-center py-4 text-sm text-tertiary">
								Carregando mais...
							</div>
						) : null}
					</div>
				</TableCard.Root>
			);
		}

		return (
			<div className="px-6 pb-6">
				<EmptyState className="min-h-40 w-full py-6" size="md">
					<EmptyState.Header pattern="none">
						<EmptyState.FeaturedIcon
							className="text-brand-500"
							color="brand"
							icon={Link03}
							theme="modern"
						/>
					</EmptyState.Header>
					<EmptyState.Content>
						<EmptyState.Title>Nenhuma atribuição encontrada</EmptyState.Title>
						<EmptyState.Description>{description}</EmptyState.Description>
					</EmptyState.Content>
				</EmptyState>
			</div>
		);
	}

	return (
		<div className="z-0 flex flex-col gap-3 rounded-[20px] border border-secondary_alt border-solid bg-bg-secondary">
			<div className="flex flex-nowrap items-center justify-between gap-2 p-6 pb-0">
				<div>
					<h3 className="font-medium text-gray-900 text-sm">
						Atribuições de Veículo
					</h3>
					<p className="text-gray-600 text-xs">
						Histórico de atribuições de reboques e/ou semirreboques a veículos
					</p>
				</div>
				{canAssign ? (
					<PageButton
						color="link-color"
						iconLeading={Link03}
						onClick={() => setIsAssignModalOpen(true)}
						size="sm"
					>
						Atribuir a veículo
					</PageButton>
				) : null}
			</div>

			{renderContent()}

			{canAssign ? (
				<AssignTrailerModal
					assignedTrailerIds={assignedTrailerIds}
					isOpen={isAssignModalOpen}
					onClose={() => setIsAssignModalOpen(false)}
					onSuccess={() => refetch()}
					vehicleId={vehicleId}
					vehiclePlate={vehiclePlate}
				/>
			) : null}

			<ModalOverlay
				isOpen={assignmentToRemove !== null}
				onOpenChange={(open) => {
					if (!open) {
						setAssignmentToRemove(null);
					}
				}}
			>
				<Modal className="relative mx-auto h-auto max-w-[400px]! gap-0 overflow-hidden rounded-2xl bg-bg-primary p-6">
					<ButtonUtility
						className="absolute top-5 right-5"
						color="tertiary"
						icon={<XClose className="size-5 text-quaternary" />}
						onClick={() => setAssignmentToRemove(null)}
						tooltip="Fechar"
					/>

					<div className="pointer-events-none relative mb-4 flex items-center justify-center">
						<FeaturedIcon color="warning" icon={AlertCircle} size="lg" />
						{[20, 28, 36, 44, 52].map((size) => (
							<div
								className="absolute rounded-full border border-gray-200"
								key={size}
								style={{
									height: `${size * 4}px`,
									width: `${size * 4}px`,
									opacity: (70 - size / 2) / 100,
								}}
							/>
						))}
					</div>

					<h1 className="mb-2 font-semibold text-base text-gray-900">
						Retirar atribuição atual?
					</h1>
					{assignmentToRemove ? (
						<p className="mb-6 text-gray-600 text-sm">
							O veículo <strong>{vehiclePlate}</strong> possui vínculo com o
							Reboque{" "}
							<strong>{getAssignedPlate(assignmentToRemove, vehicleId)}</strong>
							. Ao clicar em Retirar Atribuição, o veículo ficará sem essa
							atribuição.
						</p>
					) : null}

					<div className="flex flex-row gap-3">
						<Button
							className="flex-1"
							disabled={deleteAssignment.isPending}
							onClick={() => setAssignmentToRemove(null)}
							size="lg"
							type="button"
							variant="secondary"
						>
							Cancelar
						</Button>
						<Button
							className="flex-1"
							disabled={deleteAssignment.isPending}
							onClick={handleConfirmRemove}
							size="lg"
							type="button"
						>
							Retirar Atribuição
						</Button>
					</div>
				</Modal>
			</ModalOverlay>
		</div>
	);
};
